import { RefObject, useRef } from 'react';
import { useMeasure, useScroll } from 'react-use';
import { MdKeyboardArrowRight, MdKeyboardArrowLeft } from 'react-icons/md';
import { IconButton, ScrollShadow } from '@ds';
import { useCasesBoardStore } from '@/store/cases-board.store';
import { caseStatusNames } from '@/constants/case/case.constants';
import styles from './CasesBoardNavigationChips.module.scss';
import { CasesBoardNavigationChip } from './CasesBoardNavigationChip';

type CasesBoardNavigationChipsProps = {
  onChipClick: (status: string) => void;
};

export const CasesBoardNavigationChips = ({ onChipClick }: CasesBoardNavigationChipsProps) => {
  const statusesOrdered = useCasesBoardStore((store) => store.statusesOrdered);
  const groupsOrdered = useCasesBoardStore((store) => store.groupsOrdered);
  const showGroups = useCasesBoardStore((store) => store.showGroups);
  const chipsListRef = useRef<HTMLDivElement>(null);
  const { x: scrollX } = useScroll(chipsListRef as RefObject<HTMLElement>);
  const [trackRef] = useMeasure<HTMLDivElement>();

  const prevBtnDisabled = scrollX === 0;
  const nextBtnDisabled = chipsListRef.current
    ? chipsListRef.current.clientWidth + scrollX >= chipsListRef.current.scrollWidth
    : false;

  const scrollChipsList = (dir: 'forward' | 'back') => {
    chipsListRef.current?.scrollBy({
      left: (chipsListRef.current.clientWidth / 2) * (dir === 'forward' ? 1 : -1),
      behavior: 'smooth',
    });
  };

  return (
    <div className={styles.chipsContainer}>
      <IconButton
        startContent={<MdKeyboardArrowLeft />}
        size="xs"
        isDisabled={prevBtnDisabled}
        variant="outline"
        className="min-w-6"
        onClick={() => scrollChipsList('back')}
      />
      <ScrollShadow ref={chipsListRef} orientation="horizontal" hideScrollBar className={styles.chipsList}>
        <div ref={trackRef} className="flex gap-2">
          {showGroups
            ? groupsOrdered.map((g) => (
                <CasesBoardNavigationChip key={g.id} id={g.id} title={g.name} group={g} onChipClick={onChipClick} />
              ))
            : statusesOrdered.map((status) => (
                <CasesBoardNavigationChip
                  key={status}
                  id={status}
                  title={caseStatusNames[status]}
                  status={status}
                  onChipClick={onChipClick}
                />
              ))}
        </div>
      </ScrollShadow>
      <IconButton
        startContent={<MdKeyboardArrowRight />}
        size="xs"
        isDisabled={nextBtnDisabled}
        variant="outline"
        className="min-w-6"
        onClick={() => scrollChipsList('forward')}
      />
    </div>
  );
};
